/**
 * ProfileGate — blokada marketingu gdy profil niekompletny
 * Wymusza uzupełnienie profilu przed dostępem do kampanii/auto-publikacji
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ArrowRight, User, Camera, FileText, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/utils/api';

const REQUIRED_FIELDS = [
  { key: 'business_name', label: 'Nazwa', icon: User, tab: 'basic' },
  { key: 'city', label: 'Lokalizacja', icon: MapPin, tab: 'basic' },
  { key: 'phone', label: 'Telefon', icon: Phone, tab: 'basic' },
  { key: 'description', label: 'Opis', icon: FileText, tab: 'about' },
  { key: 'main_photo', label: 'Zdjęcie główne', icon: Camera, tab: 'photos' },
];

const MIN_PROGRESS = 50;

export default function ProfileGate({ children }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | blocked | ok
  const [missing, setMissing] = useState([]);
  const [progress, setProgress] = useState(0);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) { setStatus('ok'); return; }
    const check = async () => {
      try {
        const res = await axios.get(`${API}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = res.data;
        const prog = p.progress || 0;
        setProgress(prog);

        const miss = REQUIRED_FIELDS.filter(f => {
          const val = p[f.key];
          return !val || val === '' || (Array.isArray(val) && val.length === 0);
        });
        setMissing(miss);

        if (prog < MIN_PROGRESS || miss.length >= 3) {
          setStatus('blocked');
        } else {
          setStatus('ok');
        }
      } catch {
        setStatus('ok');
      }
    };
    check();
  }, [token]);

  if (status === 'loading') return null;
  if (status === 'ok') return children;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-[60vh] flex items-center justify-center p-6"
        data-testid="profile-gate"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl border border-zinc-200 shadow-[0_8px_40px_rgba(0,0,0,0.08)] max-w-lg w-full overflow-hidden"
        >
          {/* Header */}
          <div className="h-1.5 bg-gradient-to-r from-[#F47A3B] via-[#F9A66C] to-[#F47A3B]" />
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[#F47A3B]" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">
              Najpierw uzupełnij profil
            </h2>
            <p className="text-[14px] text-zinc-500 mb-6 max-w-sm mx-auto leading-relaxed">
              System potrzebuje Twoich danych, żeby przygotować skuteczne kampanie. Uzupełnij brakujące informacje.
            </p>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] text-zinc-400">Kompletność profilu</span>
                <span className="text-[12px] font-bold text-[#F47A3B]">{progress}%</span>
              </div>
              <div className="h-2.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #F47A3B, #F9A66C)' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </div>

            {/* Missing fields */}
            <div className="space-y-2 mb-6">
              {missing.map((m, i) => {
                const Icon = m.icon;
                return (
                  <motion.div
                    key={m.key}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/15 text-left"
                  >
                    <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-[13px] font-medium text-amber-800">{m.label}</span>
                    <span className="text-[11px] text-amber-600 ml-auto">brakuje</span>
                  </motion.div>
                );
              })}
            </div>

            <Button
              onClick={() => {
                const firstTab = missing[0]?.tab || 'basic';
                navigate(`/panel/profil?tab=${firstTab}`);
              }}
              className="w-full bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl py-3 text-[14px] font-semibold"
              data-testid="profile-gate-cta"
            >
              Uzupełnij profil
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
